import { Driver, WayPoint, Task as BringgTask, AlertSeverity } from '@bringg/types';
import { action, computed, observable, toJS, makeObservable } from 'mobx';
import { getRoot } from 'mobx-easy';
import { pick, get } from 'lodash';

import { getRouteLabel } from 'bringg-web/features/exceptions-management/utils';
import { ElasticAlert, Exception } from '../../../types/common.consts';
import { EMPTY_ROUTE_ID, EMPTY_TEAM_ID } from '../../../consts';
import type RootStore from '../../root-store';
import { getActionableWaypoint } from 'bringg-web/stores/tasks/task-utils';
import { workflowsRootStore } from 'bringg-web/features/automation-workflows/stores/workflows-root-store';
import { timezoneProvider } from 'bringg-web/services/timezone/timezone-provider';

interface Alert extends ElasticAlert {
	team_id: number;
	severity: AlertSeverity;
}

const TASK_FIELDS_TO_MIGRATE: (keyof BringgTask)[] = [
	'external_id',
	'required_skills',
	'user_id',
	'fleet_id',
	'team_ids',
	'status',
	'priority',
	'title',
	'scheduled_at',
	'created_at',
	'way_points',
	'active_way_point_id',
	'aggregations'
];

// TODO: check task.ts observable fields, and figure out if we need to react on all that field changes
// if no, we need to create computed inside task-domain-object
// test changes for activityLog i.e. cause seems like it is changed frequently
class Alert {
	owner_id: number;

	constructor(alert: ElasticAlert) {
		makeObservable(this, {
			owner_id: observable,
			exception: computed,
			toJSObject: computed,
			timezone: computed,
			actionableWayPoint: computed,
			driver: computed,
			set: action
		});

		Object.assign(this, alert);
		this.team_id = get(alert, ['team_ids', 0], EMPTY_TEAM_ID);

		// Note: consider possible race condition if workflowAlertsStore isn't ready
		const customAlert = workflowsRootStore.getStore().workflowAlertsStore.getAlertById(alert.alert_type);
		this.severity = customAlert?.severity ?? AlertSeverity.Medium;

		this.id = this.uuid;
	}

	// Exceptions alert (only related to a task) with some task fields
	// Recomputed each time if the component that uses it is not wrapped with observer
	// Still recomputed for each row initial render on scroll (check virtualization / memoization)
	get exception(): Exception {
		const { task_id, created_at, alert_type, uuid, extra_data, owner_id, severity } = this;
		const { alertsStore, runStore, plannedRoutesStore } = getRoot<RootStore>().data;

		if (!task_id) {
			return null;
		}

		// TODO: figure out if we still need to split all tasks on open / planning / all in tasks store
		// this was designed as an optimization, but since we are using here pool of all tasks
		// optimization, if there was any, is not applicable anymore
		const relatedTask = toJS(alertsStore.getTasks.get(task_id));
		if (!relatedTask) {
			return null;
		}

		const run = relatedTask.run_id ? runStore.get(relatedTask.run_id) : undefined;
		const plannedRoute = plannedRoutesStore.get(run?.planned_route_id);

		const routeId = relatedTask.run_id || EMPTY_ROUTE_ID;
		const routeTitle = getRouteLabel(routeId, run, plannedRoute);

		return {
			...pick(relatedTask, TASK_FIELDS_TO_MIGRATE),
			// TODO: this whole trick with assign `id` to `uuid` is needed because of elasticsearch
			// because in some cases we don't have a id in response and anyway we should use `uuid` for API calls
			id: uuid,
			alert_type,
			task_id,
			team_id: relatedTask.team_ids?.length ? relatedTask.team_ids[0] : EMPTY_TEAM_ID,
			alert_time: created_at,
			routeId,
			routeTitle,
			// extra_data in @bringg/types defined as `object` that's not allowed to direct to any property of extra_data
			extra_data: extra_data as Record<string, unknown>,
			owner_id,
			severity
		};
	}

	// TODO: check the need to strip methods from resulting object, which is logical for the most cases
	// Alert methods maybe still there after toJS
	get toJSObject() {
		return toJS(this);
	}

	get timezone(): string {
		if (!this.task_id) {
			return null;
		}
		const task = getRoot<RootStore>().data.alertsStore.getTasks.get(this.task_id);
		return task ? timezoneProvider.getTimezoneByTask(task) : null;
	}

	get actionableWayPoint(): WayPoint {
		if (!this.task_id) {
			return null;
		}
		const task = getRoot<RootStore>().data.alertsStore.getTasks.get(this.task_id);
		return task ? getActionableWaypoint(task) : null;
	}

	get driver(): Driver {
		if (!this.task_id) {
			return null;
		}
		const task = getRoot<RootStore>().data.alertsStore.getTasks.get(this.task_id);
		return task ? getRoot<RootStore>().data.driversStore.items.get(task.user_id) : null;
	}

	get company_id() {
		if (!this.task_id) {
			return null;
		}
		const { alertsStore, driversStore } = getRoot<RootStore>().data;

		const task = alertsStore.getTasks.get(this.task_id);
		const taskDriver = driversStore.get(task?.user_id);
		return taskDriver?.company_id;
	}

	set = (updater: Partial<ElasticAlert>) => {
		Object.assign(this, updater);
	};
}

export default Alert;
